/**
 * Files Store
 * Feature: 006-file-management-ui
 * 
 * MobX store for file management state
 */

import { makeAutoObservable, runInAction, observable } from 'mobx';
import filesService from '../services/files.service';
import type {
  FileResponse,
  FileDetailResponse,
  FileCategory,
  StorageUsageResponse,
  StorageBreakdownResponse,
  UploadItem,
  FileFilters,
  PreviewResponse,
} from '../types/files';

export default class FilesStore {
  // File list state
  files: FileResponse[] = [];
  totalFiles = 0;
  currentPage = 1;
  pageSize = 20;
  isLoading = false;
  error: string | null = null;

  // Filters
  filters: FileFilters = {
    search: '',
    category: null,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  };

  // Storage
  storageUsage: StorageUsageResponse | null = null;
  storageBreakdown: StorageBreakdownResponse | null = null;
  isLoadingStorage = false;

  // Selection
  selectedFileIds: Set<string> = observable.set(new Set<string>());

  // Upload queue
  uploadQueue: UploadItem[] = [];

  // Selected file for details/preview
  selectedFile: FileDetailResponse | null = null;
  isLoadingDetails = false;

  // Preview state
  previewData: PreviewResponse | null = null;
  isLoadingPreview = false;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed values
  get selectedCount() {
    return this.selectedFileIds.size;
  }

  get totalPages() {
    return Math.ceil(this.totalFiles / this.pageSize);
  }

  get hasNextPage() {
    return this.currentPage < this.totalPages;
  }

  get hasPrevPage() {
    return this.currentPage > 1;
  }

  get isAllSelected() {
    return this.files.length > 0 && this.files.every(f => this.selectedFileIds.has(f.id));
  }

  get selectedFiles() {
    return this.files.filter(f => this.selectedFileIds.has(f.id));
  }

  get canBulkDelete() {
    // Can only delete files without dependencies
    return this.selectedFiles.some(f => (f.dependencyCount ?? 0) === 0);
  }

  // Actions
  async fetchFiles() {
    this.isLoading = true;
    this.error = null;
    try {
      const response = await filesService.listFiles({
        page: this.currentPage,
        limit: this.pageSize,
        search: this.filters.search || undefined,
        category: this.filters.category || undefined,
        sortBy: this.filters.sortBy,
        sortOrder: this.filters.sortOrder,
      });
      runInAction(() => {
        this.files = response.data.data;
        this.totalFiles = response.data.meta.total;
      });
    } catch (err) {
      runInAction(() => {
        this.error = 'Failed to load files';
        console.error('Error fetching files:', err);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchFileDetails(fileId: string) {
    this.isLoadingDetails = true;
    try {
      const response = await filesService.getFileWithDependencies(fileId);
      runInAction(() => {
        this.selectedFile = response.data;
      });
    } catch (err) {
      console.error('Error fetching file details:', err);
    } finally {
      runInAction(() => {
        this.isLoadingDetails = false;
      });
    }
  }

  async fetchPreview(fileId: string) {
    this.isLoadingPreview = true;
    this.previewData = null;
    try {
      const response = await filesService.getPreview(fileId);
      runInAction(() => {
        this.previewData = response.data;
      });
    } catch (err) {
      console.error('Error fetching preview:', err);
    } finally {
      runInAction(() => {
        this.isLoadingPreview = false;
      });
    }
  }

  async fetchStorageUsage() {
    this.isLoadingStorage = true;
    try {
      const response = await filesService.getStorageUsage();
      runInAction(() => {
        this.storageUsage = response.data;
      });
    } catch (err) {
      console.error('Error fetching storage usage:', err);
    } finally {
      runInAction(() => {
        this.isLoadingStorage = false;
      });
    }
  }

  async fetchStorageBreakdown() {
    try {
      const response = await filesService.getStorageBreakdown();
      runInAction(() => {
        this.storageBreakdown = response.data;
      });
    } catch (err) {
      console.error('Error fetching storage breakdown:', err);
    }
  }

  async deleteFile(fileId: string): Promise<boolean> {
    try {
      await filesService.deleteFile(fileId);
      runInAction(() => {
        this.files = this.files.filter(f => f.id !== fileId);
        this.totalFiles -= 1;
        this.selectedFileIds.delete(fileId);
      });
      // Refresh storage after delete
      this.fetchStorageUsage();
      return true;
    } catch (err) {
      console.error('Error deleting file:', err);
      return false;
    }
  }

  async bulkDeleteFiles(): Promise<{ deleted: number; skipped: number }> {
    try {
      const fileIds = Array.from(this.selectedFileIds);
      const response = await filesService.bulkDelete({ fileIds });
      const { deletedCount, skippedCount, deleted } = response.data;
      
      runInAction(() => {
        // Remove deleted files from the list
        const deletedIds = new Set(deleted.map(d => d.id));
        this.files = this.files.filter(f => !deletedIds.has(f.id));
        this.totalFiles -= deletedCount;
        this.selectedFileIds.clear();
      });
      
      // Refresh storage after bulk delete
      this.fetchStorageUsage();
      
      return { deleted: deletedCount, skipped: skippedCount };
    } catch (err) {
      console.error('Error bulk deleting files:', err);
      return { deleted: 0, skipped: 0 };
    }
  }

  // Pagination
  setPage(page: number) {
    this.currentPage = page;
    this.fetchFiles();
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
    this.fetchFiles();
  }

  nextPage() {
    if (this.hasNextPage) {
      this.setPage(this.currentPage + 1);
    }
  }

  prevPage() {
    if (this.hasPrevPage) {
      this.setPage(this.currentPage - 1);
    }
  }

  // Filters
  setFilters(filters: Partial<FileFilters>) {
    this.filters = { ...this.filters, ...filters };
    this.currentPage = 1;
    this.fetchFiles();
  }

  setSearch(search: string) {
    this.setFilters({ search });
  }

  setCategory(category: FileCategory | null) {
    this.setFilters({ category });
  }

  setSortBy(sortBy: FileFilters['sortBy']) {
    this.setFilters({ sortBy });
  }

  setSortOrder(sortOrder: FileFilters['sortOrder']) {
    this.setFilters({ sortOrder });
  }

  resetFilters() {
    this.filters = {
      search: '',
      category: null,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    };
    this.currentPage = 1;
    this.fetchFiles();
  }

  // Selection
  toggleSelection(fileId: string) {
    if (this.selectedFileIds.has(fileId)) {
      this.selectedFileIds.delete(fileId);
    } else {
      this.selectedFileIds.add(fileId);
    }
    // Force reactivity update
    this.selectedFileIds = observable.set(new Set(this.selectedFileIds));
  }

  selectAll() {
    this.files.forEach(f => this.selectedFileIds.add(f.id));
    this.selectedFileIds = observable.set(new Set(this.selectedFileIds));
  }

  deselectAll() {
    this.selectedFileIds = observable.set(new Set<string>());
  }

  clearSelection() {
    this.selectedFileIds = observable.set(new Set<string>());
  }

  // Upload queue management
  addToUploadQueue(item: UploadItem) {
    this.uploadQueue.push(item);
  }

  updateUploadProgress(itemId: string, progress: number) {
    const item = this.uploadQueue.find(i => i.id === itemId);
    if (item) {
      item.progress = progress;
    }
  }

  updateUploadStatus(itemId: string, status: UploadItem['status'], error?: string) {
    const item = this.uploadQueue.find(i => i.id === itemId);
    if (item) {
      item.status = status;
      if (error) item.error = error;
    }
  }

  setUploadedFile(itemId: string, file: FileResponse) {
    const item = this.uploadQueue.find(i => i.id === itemId);
    if (item) {
      item.uploadedFile = file;
      item.status = 'completed';
    }
  }

  removeFromUploadQueue(itemId: string) {
    this.uploadQueue = this.uploadQueue.filter(i => i.id !== itemId);
  }

  clearUploadQueue() {
    this.uploadQueue = [];
  }

  // Details panel
  clearSelectedFile() {
    this.selectedFile = null;
    this.previewData = null;
  }

  // Reset store
  reset() {
    this.files = [];
    this.totalFiles = 0;
    this.currentPage = 1;
    this.isLoading = false;
    this.error = null;
    this.filters = {
      search: '',
      category: null,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    };
    this.storageUsage = null;
    this.storageBreakdown = null;
    this.selectedFileIds = observable.set(new Set<string>());
    this.uploadQueue = [];
    this.selectedFile = null;
    this.previewData = null;
  }
}

// Export singleton instance
export const filesStore = new FilesStore();
